import {Label} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import styled from 'styled-components';

import {isNotNil} from 'ramda-adjunct';

import {AuctionVehicleDetailResponseBody} from '@dms/api/core';
import i18n from '@dms/i18n';

import {EMPTY_PLACEHOLDER, suffixTestId, TestIdProps} from 'shared/web';

import {useApiEnums} from '../../../hooks/useApiEnums';
import {calculatePriceExclVat} from '../../../utils/calculatePriceExclVat';
import {isHiddenAuctionType} from '../../../utils/isHiddenAuctionType';
import {formatVatRate} from '../utils/formatVatRate';

/**
 * Narrowest width (in space steps, 52 = 208px) of one label/value row. The longest label may wrap
 * onto a second line at this width, which is preferred over dropping to a single column early.
 */
const PRICING_ITEM_MIN_WIDTH = 52;
/** Same horizontal gap the platform `Grid` used here before. */
const PRICING_COLUMN_GAP = 4;
/** Half of the gap, which each of the two columns gives up. */
const PRICING_COLUMN_HALF_GAP = 2;

/**
 * Two columns of label/value rows while both fit the preview, otherwise one column with the rows
 * under each other. `max(minimum, 50% - gap)` caps the auto-fit grid at two columns even in a wide
 * preview (each column takes half of the width minus its half of the gap), and `min(100%, ...)`
 * keeps the single column inside a preview narrower than the minimum; the platform `Grid` only
 * switches the column count by device.
 */
const PricingGrid = styled.div`
  display: grid;
  /* TODO T20-140975 Extend platform's Grid with a minimal column width (auto-fit) capped at a column count and use it here */
  grid-template-columns: repeat(
    auto-fit,
    minmax(
      min(
        100%,
        max(
          ${({theme}) => theme.getSize(PRICING_ITEM_MIN_WIDTH)},
          calc(50% - ${({theme}) => theme.getSize(PRICING_COLUMN_HALF_GAP)})
        )
      ),
      1fr
    )
  );
  column-gap: ${({theme}) => theme.getSize(PRICING_COLUMN_GAP)};
`;

interface AuctionPricingGridProps extends TestIdProps {
  auction: AuctionVehicleDetailResponseBody;
}

export function AuctionPricingGrid(props: AuctionPricingGridProps) {
  const formatCurrency = useFormatCurrency();
  const auction = props.auction;
  const auctionVehicle = auction.auctionVehicle;
  const isHiddenType = isHiddenAuctionType(auctionVehicle.auctionType);
  const isVatDeductible = !!auctionVehicle.vatReclaimable;
  const {getEnum} = useApiEnums();
  const countryName =
    getEnum('country').find(({key}) => key === auctionVehicle.sellerCountry)?.label ??
    auctionVehicle.sellerCountry ??
    '';
  const priceExclVat = isVatDeductible
    ? calculatePriceExclVat(auctionVehicle.originalPriceActual, auctionVehicle.vatRate)
    : null;

  const formattedConvertedPrice = isNotNil(auctionVehicle.priceActual)
    ? (formatCurrency(auctionVehicle.priceActual, auctionVehicle.currency ?? '') ??
      EMPTY_PLACEHOLDER)
    : EMPTY_PLACEHOLDER;

  const formattedOriginalPrice = isNotNil(auctionVehicle.originalPriceActual)
    ? (formatCurrency(auctionVehicle.originalPriceActual, auctionVehicle.originalCurrency ?? '') ??
      EMPTY_PLACEHOLDER)
    : EMPTY_PLACEHOLDER;

  const formattedPriceExclVat = isNotNil(priceExclVat)
    ? (formatCurrency(priceExclVat, auctionVehicle.originalCurrency ?? '') ?? EMPTY_PLACEHOLDER)
    : EMPTY_PLACEHOLDER;

  const pricingGridItems = {
    ...(!isHiddenType
      ? {
          convertedPrice: {
            label: i18n.t('general.labels.priceWithVat'),
            value: formattedConvertedPrice,
          },
        }
      : {}),
    carId: {
      label: i18n.t('entity.auction.labels.carId'),
      value: auctionVehicle.pk ?? EMPTY_PLACEHOLDER,
    },
    ...(!isHiddenType
      ? {
          originalPrice: {
            label: i18n.t('entity.auction.labels.origCurrencyWithVat'),
            value: formattedOriginalPrice,
          },
        }
      : {}),
    auctionId: {
      label: i18n.t('entity.auction.labels.auctionIdLabel'),
      value: auctionVehicle.auctionId ?? EMPTY_PLACEHOLDER,
    },
    ...(!isHiddenType && isVatDeductible
      ? {
          priceExclVat: {
            label: i18n.t('entity.auction.labels.origCurrencyWithoutVat'),
            value: formattedPriceExclVat,
          },
        }
      : {}),
    vin: {
      label: i18n.t('entity.vehicle.labels.vin'),
      value: auctionVehicle.vin ?? EMPTY_PLACEHOLDER,
    },
    vatDeductible: {
      label: i18n.t('general.labels.vatDeductible'),
      value: isVatDeductible ? i18n.t('general.labels.yes') : i18n.t('general.labels.no'),
    },
  } as const;

  return (
    <VStack spacing={2}>
      <PricingGrid>
        {Object.entries(pricingGridItems).map(([testId, item]) => (
          <Box
            key={testId}
            paddingVertical={1}
            borderBottom="1px solid"
            borderColor="general.separator"
          >
            <HStack justify="space-between" align="center">
              <Label>{item.label}</Label>
              <Text size="small" color="primary" data-testid={suffixTestId(testId, props)}>
                {item.value}
              </Text>
            </HStack>
          </Box>
        ))}
      </PricingGrid>
      <Show when={!isHiddenType && isVatDeductible && isNotNil(auctionVehicle.vatRate)}>
        <Text size="xSmall" color="tertiary" data-testid={suffixTestId('vatFooter', props)}>
          {i18n.t('entity.auction.labels.inclVat', {
            vatRate: formatVatRate(auctionVehicle.vatRate ?? 0),
            country: countryName,
          })}
        </Text>
      </Show>
    </VStack>
  );
}
